// ── Shared bulk-action toolbar (TPT9098) ──
//
// One selection model and one toolbar for every web board that lets a user tick several
// tasks and change them together. createBulkSelection() owns the selected task keys;
// renderBulkToolbar() draws a sticky toolbar into a host element while one or more keys are
// selected, and applies each action to the whole selection in ONE request against the bulk
// endpoints (TPT9097, see tt-api-tasks.md § Bulk Update and Delete): PATCH /tasks/bulk for
// Sprint, Assignee, Status, Type, Add tag and Remove tag, POST /tasks/bulk-delete for Delete.
// Nothing is applied optimistically — the server decides which tasks change (it skips
// unchanged ones and priority moves that break the dependency floor), and the host merges
// the response through onApplied(result). See tt-web-backlog.md § Bulk action toolbar.

import { api } from '../api.js';
import { getUser } from '../auth.js';
import { showToast, openModal, closeModal } from '../ui.js';
import { escapeHtml, TYPES, TYPE_LABELS, canStartTask } from '../utils.js';
import { renderMemberCombo, createMemberCombo } from './member-combo.js';
import { attachTagTypeahead } from '../lib/tag-typeahead.js';
import { loadStatuses, statusLabel, roleStatus } from '../lib/statuses.js';
import { sprintsEnabled } from '../lib/sprints-enabled.js';
import { groupLabelSingular, groupLabelPlural, groupContainerTitle } from '../lib/task-group-label.js';
import { railTargets } from '../lib/backlog-rail.js';
import { can } from '../lib/permissions.js';

// Mirrors BULK_MAX_TASKS in api/src/lib/task-bulk.js: a request names at most this many keys,
// so a larger selection goes out in consecutive chunks (sendInChunks() below).
export const BULK_MAX_TASKS = 200;

// Response keys naming tasks OTHER than the selected ones that the server moved or reopened
// (the C1338/C1431 parent and C1464/TPT2 sprint cascades) — a host that sees any of them
// should refetch rather than trust the returned rows alone.
export const BULK_CASCADE_KEYS = Object.freeze([
  'children_rescheduled', 'parent_rescheduled', 'parent_auto_completed', 'parent_auto_reopened',
]);

// escapeHtml() escapes & < > but not '"' — needed for values written into attributes.
function escapeAttr(s) {
  return escapeHtml(s).replace(/"/g, '&quot;');
}

// ── Selection model ──
//
// The selected task keys, in the order they were picked, plus change listeners. Every
// mutator notifies once per call and only when the set actually changed, so a host can
// re-sync checkboxes and the toolbar can re-count from one listener each.
export function createBulkSelection(initialKeys = []) {
  const keys = new Set();
  const listeners = new Set();
  for (const key of initialKeys || []) if (key != null) keys.add(key);

  function emit() {
    const snapshot = [...keys];
    for (const fn of [...listeners]) fn(snapshot);
  }

  const selection = {
    get size() { return keys.size; },
    has: (key) => keys.has(key),
    keys: () => [...keys],
    add(key) {
      if (key == null || keys.has(key)) return false;
      keys.add(key);
      emit();
      return true;
    },
    delete(key) {
      if (!keys.delete(key)) return false;
      emit();
      return true;
    },
    // force: true selects, false deselects, omitted flips. Returns the key's new state.
    toggle(key, force) {
      const on = force === undefined ? !keys.has(key) : !!force;
      if (on) selection.add(key);
      else selection.delete(key);
      return on;
    },
    addAll(list) {
      let changed = false;
      for (const key of list || []) {
        if (key != null && !keys.has(key)) { keys.add(key); changed = true; }
      }
      if (changed) emit();
      return changed;
    },
    deleteAll(list) {
      let changed = false;
      for (const key of list || []) if (keys.delete(key)) changed = true;
      if (changed) emit();
      return changed;
    },
    // Keeps only the keys `keep(key)` accepts — a host drops keys whose rows it no longer shows.
    retain(keep) {
      let changed = false;
      for (const key of [...keys]) if (!keep(key)) { keys.delete(key); changed = true; }
      if (changed) emit();
      return changed;
    },
    clear() {
      if (keys.size === 0) return false;
      keys.clear();
      emit();
      return true;
    },
    // fn(keys) after every change; returns the unsubscribe function.
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
  return selection;
}

// ── Pure builders (markup and messages) ──

const ICONS = {
  check: '<polyline points="20 6 9 17 4 12"/>',
  sprint: '<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>',
  assignee: '<path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  status: '<circle cx="12" cy="12" r="9"/><polyline points="8 12.5 11 15.5 16 9.5"/>',
  type: '<circle cx="7" cy="7" r="4"/><rect x="13" y="3" width="8" height="8" rx="1.5"/><path d="M12 13l4.5 8h-9z"/>',
  tag: '<path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>',
  trash: '<polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>',
  close: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
};

function icon(name) {
  return `<svg class="bulk-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">${ICONS[name]}</svg>`;
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

// "TPT12" for one key, "3 tasks" for more.
function subjectLabel(keys) {
  return keys.length === 1 ? keys[0] : plural(keys.length, 'task');
}

// Up to three keys, then "+N more".
function keyList(keys) {
  const shown = keys.slice(0, 3).join(', ');
  return keys.length > 3 ? `${shown} +${keys.length - 3} more` : shown;
}

function quoted(names) {
  return (names || []).map(n => `“${n}”`).join(', ');
}

// Sprint <option>s: a hidden placeholder, Backlog (when the host offers it), then every
// sprint number in use — railTargets(): sprints rows plus `rows`' positive priorities, closed
// ones kept and marked — and a trailing "+ New <Singular>" at nextSprintNumber().
export function sprintSelectOptions({ sprints = [], rows = [], singular = 'Sprint', pluralLabel = 'Sprints', includeBacklog = true } = {}) {
  let html = `<option value="" selected disabled hidden>${escapeHtml(singular)}…</option>`;
  if (includeBacklog) html += '<option value="0">Backlog</option>';
  html += `<optgroup label="${escapeAttr(pluralLabel)}">`;
  for (const target of railTargets(sprints, rows)) {
    const label = target.isNew
      ? `+ New ${singular}`
      : groupContainerTitle(target.number, (sprints || []).find(s => s.number === target.number), singular)
        + (target.closed ? ' (closed)' : '');
    html += `<option value="${target.number}">${escapeHtml(label)}</option>`;
  }
  return html + '</optgroup>';
}

// Status <option>s in the project's display_order (loadStatuses() already sorts them).
export function statusSelectOptions(statuses = []) {
  return '<option value="" selected disabled hidden>Status…</option>'
    + (statuses || []).map(s => `<option value="${escapeAttr(s.name)}">${escapeHtml(statusLabel(s.name))}</option>`).join('');
}

export function typeSelectOptions() {
  return '<option value="" selected disabled hidden>Type…</option>'
    + TYPES.map(t => `<option value="${t}">${escapeHtml(TYPE_LABELS[t])}</option>`).join('');
}

// The toolbar's inner markup. Every select is an ACTION, not a bound field: it shows its
// placeholder, and picking an option applies it to the whole selection and resets it.
export function bulkToolbarHtml({
  id = 'bulk-toolbar', count = 0, singular = 'Sprint', showSprint = true, canDelete = true,
  sprintOptions = '', statusOptions = '', members = [],
} = {}) {
  let html = '<span class="bulk-toolbar-count" aria-live="polite" aria-atomic="true">'
    + `<span class="bulk-toolbar-count-badge">${icon('check')}</span>`
    + `<span class="bulk-toolbar-count-num">${count}</span> selected</span>`;

  html += '<div class="bulk-toolbar-controls">';
  if (showSprint) {
    html += `<label class="bulk-ctl bulk-ctl--sprint" title="Move to ${escapeAttr(singular)}">${icon('sprint')}`
      + `<select class="bulk-ctl-select" data-bulk="priority" aria-label="Move to ${escapeAttr(singular)}">${sprintOptions}</select></label>`;
  }
  html += `<div class="bulk-ctl bulk-ctl--assignee" title="Assign to">${icon('assignee')}`
    + renderMemberCombo({ id: `${id}-assignee`, assignee: null, members })
    + '</div>';
  html += `<label class="bulk-ctl bulk-ctl--status" title="Set status">${icon('status')}`
    + `<select class="bulk-ctl-select" data-bulk="status" aria-label="Set status">${statusOptions}</select></label>`;
  html += `<label class="bulk-ctl bulk-ctl--type" title="Set type">${icon('type')}`
    + `<select class="bulk-ctl-select" data-bulk="type" aria-label="Set type">${typeSelectOptions()}</select></label>`;
  html += `<div class="bulk-ctl bulk-ctl--tag" title="Add a tag">${icon('tag')}`
    + '<input type="text" class="bulk-ctl-tag-input" data-bulk="add_tags" placeholder="Add tag…" aria-label="Add tag"></div>';
  html += `<div class="bulk-ctl bulk-ctl--tag" title="Remove a tag">${icon('tag')}`
    + '<input type="text" class="bulk-ctl-tag-input" data-bulk="remove_tags" placeholder="Remove tag…" aria-label="Remove tag"></div>';
  html += '</div>';

  html += '<div class="bulk-toolbar-end">';
  if (canDelete) {
    html += `<button type="button" class="btn btn-sm bulk-toolbar-delete" data-bulk-action="delete">${icon('trash')}<span>Delete</span></button>`;
  }
  html += `<button type="button" class="btn btn-sm btn-ghost bulk-toolbar-clear" data-bulk-action="clear" title="Clear selection" aria-label="Clear selection">${icon('close')}<span>Clear</span></button>`;
  html += '</div>';
  html += '<span class="bulk-toolbar-progress" aria-hidden="true"></span>';
  return html;
}

// Body of the Delete confirm modal. `tasks` are the selected rows the host knows ({task_key}
// alone when it does not); deleting a task also deletes its whole subtree (C1414), said
// whenever a known row has children or the host could not tell.
export function deleteConfirmHtml(tasks = [], { knowsTasks = true } = {}) {
  const shown = tasks.slice(0, 8);
  const hasChildren = !knowsTasks || tasks.some(t => Number(t.children_count) > 0);
  let html = `<p class="bulk-delete-lead">Delete <strong>${plural(tasks.length, 'task')}</strong>? This cannot be undone.</p>`;
  html += '<ul class="bulk-delete-list">';
  for (const t of shown) {
    html += `<li><span class="bulk-delete-key">${escapeHtml(t.task_key)}</span>`
      + (t.title ? `<span class="bulk-delete-title">${escapeHtml(t.title)}</span>` : '')
      + '</li>';
  }
  if (tasks.length > shown.length) html += `<li class="bulk-delete-more">+${tasks.length - shown.length} more</li>`;
  html += '</ul>';
  if (hasChildren) html += '<p class="bulk-delete-note">Their subtasks are deleted with them.</p>';
  html += '<div class="modal-footer">'
    + '<button type="button" class="btn btn-ghost" data-bulk-confirm="cancel">Cancel</button>'
    + `<button type="button" class="btn btn-danger" data-bulk-confirm="delete">Delete ${plural(tasks.length, 'task')}</button>`
    + '</div>';
  return html;
}

// Merges one chunk's response into the running result: arrays concatenate, counts add up.
export function mergeBulkResults(into, next) {
  if (!next) return into;
  const out = { ...(into || {}) };
  for (const [key, value] of Object.entries(next)) {
    if (Array.isArray(value)) out[key] = [...(out[key] || []), ...value];
    else if (typeof value === 'number') out[key] = (out[key] || 0) + value;
    else out[key] = value;
  }
  return out;
}

// The success toast for an applied result, or '' when nothing changed. ctx: { singular,
// memberName(id), statusName(name) }.
export function appliedMessage(result, ctx = {}) {
  const singular = ctx.singular || 'Sprint';
  if (result.action === 'delete') {
    const deleted = result.deleted || [];
    if (!deleted.length) return '';
    const subtasks = (result.descendants_deleted || []).length;
    return `Deleted ${subjectLabel(deleted)}${subtasks ? ` and ${plural(subtasks, 'subtask')}` : ''}`;
  }
  const keys = result.updated || [];
  if (!keys.length) return '';
  const what = subjectLabel(keys);
  const c = result.changes || {};
  if (c.priority !== undefined) return c.priority > 0 ? `Moved ${what} to ${singular} ${c.priority}` : `Moved ${what} to Backlog`;
  if (c.assignee !== undefined) {
    if (c.assignee == null) return `Unassigned ${what}`;
    return `Assigned ${what} to ${(ctx.memberName && ctx.memberName(c.assignee)) || `#${c.assignee}`}`;
  }
  if (c.status !== undefined) return `Set ${what} to ${(ctx.statusName && ctx.statusName(c.status)) || c.status}`;
  if (c.type !== undefined) return `Changed ${what} to ${TYPE_LABELS[c.type] || c.type}`;
  if (c.add_tags) return `Tagged ${what} ${quoted(c.add_tags)}`;
  if (c.remove_tags) return `Removed ${quoted(c.remove_tags)} from ${what}`;
  return `Updated ${what}`;
}

// What "unchanged" means for this change set — the end of "TPT1, TPT2 already …".
function unchangedPhrase(changes, ctx = {}) {
  const c = changes || {};
  const singular = ctx.singular || 'Sprint';
  if (c.priority !== undefined) return c.priority > 0 ? `already in ${singular} ${c.priority}` : 'already in the Backlog';
  if (c.assignee !== undefined) {
    if (c.assignee == null) return 'already unassigned';
    return `already assigned to ${(ctx.memberName && ctx.memberName(c.assignee)) || `#${c.assignee}`}`;
  }
  if (c.status !== undefined) return `already ${(ctx.statusName && ctx.statusName(c.status)) || c.status}`;
  if (c.type !== undefined) return `already set to ${TYPE_LABELS[c.type] || c.type}`;
  if (c.add_tags) return `already tagged ${quoted(c.add_tags)}`;
  if (c.remove_tags) return `not tagged ${quoted(c.remove_tags)}`;
  return 'unchanged';
}

// The toast for skipped keys ({ text, type }), or null when none were skipped. Grouped by the
// server's reason (planBulkUpdate() in api/src/lib/task-bulk.js), plus the client-side
// `assigned_to_other`. Only "already had that value" is informational; anything else
// (a dependency floor, a key that no longer exists) is an error the user should read.
export function skippedMessage(skipped, ctx = {}) {
  const list = skipped || [];
  if (!list.length) return null;
  const singular = ctx.singular || 'Sprint';
  const groups = new Map();
  for (const s of list) {
    const reason = s.reason || 'unknown';
    if (!groups.has(reason)) groups.set(reason, []);
    groups.get(reason).push(s);
  }
  const parts = [];
  for (const [reason, items] of groups) {
    const keys = keyList(items.map(s => s.task_key));
    const one = items.length === 1;
    if (reason === 'unchanged') parts.push(`${keys} ${unchangedPhrase(ctx.changes, ctx)}`);
    else if (reason === 'not_found') parts.push(`${keys} no longer exist${one ? 's' : ''}`);
    else if (reason === 'dependency_floor') {
      parts.push(one
        ? `${keys} must stay after ${items[0].dependency} (${singular} ${items[0].dependency_priority})`
        : `${keys} must stay after their dependencies`);
    } else if (reason === 'dependent_floor') {
      parts.push(one
        ? `${keys} must stay before ${items[0].dependent} (${singular} ${items[0].dependent_priority})`
        : `${keys} must stay before the tasks that depend on them`);
    } else if (reason === 'assigned_to_other') parts.push(`${keys} ${one ? 'is' : 'are'} assigned to another member`);
    else parts.push(`${keys} ${reason.replace(/_/g, ' ')}`);
  }
  const informational = groups.size === 1 && groups.has('unchanged');
  return { text: `Skipped ${list.length}: ${parts.join('; ')}`, type: informational ? 'info' : 'error' };
}

// ── Toolbar ──

let toolbarSeq = 0;

// renderBulkToolbar(host, opts) — draws the toolbar into `host` while `opts.selection` holds
// one or more keys and the user has task:write, and empties `host` otherwise. `host` itself
// becomes the sticky element (.bulk-toolbar-host): a sticky box only travels inside its
// parent, so put it directly in a container as tall as the list it serves, outside anything
// the page rebuilds. While shown, the host's parent carries `--bulk-toolbar-h` (the toolbar's
// height) so other sticky content there can sit below it.
//
// opts:
//   selection        createBulkSelection()   required
//   projectId        number|string           required
//   getProject()     -> project              sprints_enabled, task_group_label, capabilities
//   getMembers()     -> member[]             Assignee options
//   getSprints()     -> sprint[]             Sprint options (GET /sprints rows)
//   getSprintRows()  -> {priority}[]         rows naming other sprint numbers in use
//   getTask(key)     -> task|undefined       optional: titles in the Delete confirm, the tags
//                                            Remove tag offers, the start-permission check
//   includeBacklog   boolean                 offer "Backlog" as a Sprint target (default true)
//   enqueue(run)     -> Promise              optional host write queue; default: own queue
//   onApplied(result)                        after each successful request (may return a
//                                            Promise, which the queue waits for)
//   onFailed(err, request)                   after a failed request
//   scrollEl         Element|Window          where the dropdowns reposition on scroll
//
// result: { action: 'update'|'delete', task_keys, changes, ...the endpoint's response }.
// Returns { refresh, destroy, element }.
export function renderBulkToolbar(host, opts = {}) {
  const { selection, projectId } = opts;
  if (!host || !selection || projectId == null) {
    return { refresh() {}, destroy() {}, get element() { return null; } };
  }

  const id = `bulk-toolbar-${++toolbarSeq}`;
  const scrollEl = opts.scrollEl === undefined ? window : opts.scrollEl;
  const getTask = typeof opts.getTask === 'function' ? opts.getTask : () => undefined;
  let ownQueue = Promise.resolve();
  const enqueue = typeof opts.enqueue === 'function'
    ? opts.enqueue
    : (run) => (ownQueue = ownQueue.then(run).catch(() => {}));

  let el = null;
  let assigneeCombo = null;
  let addTags = null;
  let removeTags = null;
  let resizeObserver = null;
  let statuses = [];
  let busy = 0;
  let modalToken = null;
  let destroyed = false;

  const project = () => (typeof opts.getProject === 'function' ? opts.getProject() : null);
  const members = () => (typeof opts.getMembers === 'function' ? opts.getMembers() || [] : []);
  const allowed = (cap) => can(project(), getUser(), cap);
  const singular = () => groupLabelSingular(project() && project().task_group_label);
  const isAlive = () => !destroyed && !!el && el.isConnected;

  function messageContext(changes) {
    return {
      changes,
      singular: singular(),
      memberName: (userId) => {
        const m = members().find(row => row.user_id != null && Number(row.user_id) === Number(userId));
        return m ? m.name : null;
      },
      statusName: (name) => statusLabel(name),
    };
  }

  function sprintOptions() {
    const proj = project();
    return sprintSelectOptions({
      sprints: typeof opts.getSprints === 'function' ? opts.getSprints() || [] : [],
      rows: typeof opts.getSprintRows === 'function' ? opts.getSprintRows() || [] : [],
      singular: singular(),
      pluralLabel: groupLabelPlural(proj && proj.task_group_label),
      includeBacklog: opts.includeBacklog !== false,
    });
  }

  function sync() {
    if (destroyed) return;
    if (selection.size === 0 || !allowed('task:write')) { unmount(); return; }
    if (!el) mount();
    el.querySelector('.bulk-toolbar-count-num').textContent = String(selection.size);
    if (removeTags) removeTags.refresh(); // its suggestions follow the selected tasks' tags
  }

  function mount() {
    el = document.createElement('div');
    el.className = 'bulk-toolbar';
    el.setAttribute('role', 'region');
    el.setAttribute('aria-label', 'Bulk actions');
    el.innerHTML = bulkToolbarHtml({
      id,
      count: selection.size,
      singular: singular(),
      showSprint: sprintsEnabled(project()),
      canDelete: allowed('task:delete'),
      sprintOptions: sprintOptions(),
      statusOptions: statusSelectOptions(statuses),
      members: members(),
    });
    host.replaceChildren(el);
    el.addEventListener('change', onSelectChange);
    el.addEventListener('click', onButtonClick);
    mountAssignee();
    // Registered tags only. With allowCreate the typeahead's first row would be "Create: <typed
    // text>", so Enter after "doc" would open the new-tag form instead of picking "docs"; new
    // tags are registered from the task detail panel, as the New Task modal says too.
    addTags = mountTagField('add_tags', { allowCreate: false });
    removeTags = mountTagField('remove_tags', {
      allowCreate: false,
      // Only tags a selected task carries — when the host can say which those are.
      onlyTags: opts.getTask ? selectedTaskTags : undefined,
      emptyMessage: 'No matching tags on the selected tasks',
    });
    paintBusy();
    publishHeight();
  }

  function unmount() {
    if (!el) return;
    if (assigneeCombo) { assigneeCombo.destroy(); assigneeCombo = null; }
    if (addTags) { addTags.destroy(); addTags = null; }
    if (removeTags) { removeTags.destroy(); removeTags = null; }
    if (resizeObserver) { resizeObserver.disconnect(); resizeObserver = null; }
    if (host.parentElement) host.parentElement.style.removeProperty('--bulk-toolbar-h');
    el.remove();
    el = null;
  }

  // Keeps `--bulk-toolbar-h` on the host's parent equal to the toolbar's height, which
  // changes as it wraps onto more lines at narrow widths.
  function publishHeight() {
    const parent = host.parentElement;
    if (!parent || typeof ResizeObserver !== 'function') return;
    resizeObserver = new ResizeObserver(() => {
      if (el) parent.style.setProperty('--bulk-toolbar-h', `${el.offsetHeight}px`);
    });
    resizeObserver.observe(el);
  }

  function mountAssignee() {
    const root = el.querySelector('.bulk-ctl--assignee');
    const input = root && root.querySelector('.member-combo-input');
    if (!input) return;
    input.placeholder = 'Assign to…';
    input.setAttribute('aria-label', 'Assign to');
    assigneeCombo = createMemberCombo(root, {
      getMembers: members,
      getValue: () => null, // no bound value: always blank, ready for the next pick
      setValue: () => {},
      onCommit: (userId) => applyUpdate({ assignee: userId }),
      isAlive,
      scrollEl,
    });
  }

  // A chip picked in the Add tag / Remove tag typeahead applies at once, one request per tag,
  // and stays shown until that request settles; a chip removed by hand meanwhile comes back.
  function mountTagField(field, extra) {
    const input = el.querySelector(`input[data-bulk="${field}"]`);
    if (!input) return null;
    const pending = new Set();
    const typeahead = attachTagTypeahead(input, {
      projectId,
      scrollEl,
      isAlive,
      ...extra,
      onChange: (names) => {
        for (const name of names) {
          if (pending.has(name)) continue;
          pending.add(name);
          applyUpdate({ [field]: [name] }).finally(() => {
            pending.delete(name);
            typeahead.setTags([...pending]);
          });
        }
        typeahead.setTags([...pending]);
      },
    });
    return typeahead;
  }

  function selectedTaskTags() {
    const names = new Set();
    for (const key of selection.keys()) {
      for (const name of (getTask(key) || {}).tags || []) names.add(name);
    }
    return [...names];
  }

  function onSelectChange(e) {
    const select = e.target.closest('select[data-bulk]');
    if (!select) return;
    const field = select.dataset.bulk;
    const value = select.value;
    select.selectedIndex = 0; // back to the placeholder — the select is an action, not a field
    if (value === '') return;
    if (field === 'priority') {
      const priority = Number(value);
      if (Number.isInteger(priority) && priority >= 0) applyUpdate({ priority });
    } else {
      applyUpdate({ [field]: value });
    }
  }

  function onButtonClick(e) {
    const button = e.target.closest('[data-bulk-action]');
    if (!button || button.disabled) return;
    if (button.dataset.bulkAction === 'clear') selection.clear();
    else if (button.dataset.bulkAction === 'delete') confirmDelete();
  }

  function applyUpdate(changes) {
    let keys = selection.keys();
    const blocked = [];
    // Same rule as every single-task status control (kanban, sprint board, task detail):
    // another member's task is never moved onto the in-progress-role status.
    const inProgress = roleStatus(statuses, 'in_progress');
    if (changes.status !== undefined && inProgress && changes.status === inProgress.name) {
      const me = getUser();
      keys = keys.filter((key) => {
        const task = getTask(key);
        if (task && !canStartTask(task, me)) {
          blocked.push({ task_key: key, reason: 'assigned_to_other' });
          return false;
        }
        return true;
      });
    }
    if (keys.length === 0) {
      const skipped = skippedMessage(blocked, messageContext(changes));
      if (skipped) showToast(skipped.text, skipped.type);
      return Promise.resolve();
    }
    return run('update', keys, changes, blocked);
  }

  function confirmDelete() {
    const keys = selection.keys();
    if (keys.length === 0 || !allowed('task:delete')) return;
    const tasks = keys.map(key => getTask(key) || { task_key: key });
    modalToken = openModal(`Delete ${plural(keys.length, 'task')}`, deleteConfirmHtml(tasks, { knowsTasks: !!opts.getTask }), {
      onMount(overlay) {
        const cancel = overlay.querySelector('[data-bulk-confirm="cancel"]');
        cancel.addEventListener('click', () => closeModal(modalToken));
        overlay.querySelector('[data-bulk-confirm="delete"]').addEventListener('click', () => {
          closeModal(modalToken);
          run('delete', keys, null);
        });
        cancel.focus(); // the safe choice has focus, so a stray Enter cancels
      },
    });
  }

  // One action against the server, through the host's write queue. `keys` is the selection
  // when the action was picked; a change to it while the request waits does not alter it.
  function run(action, keys, changes, preSkipped = []) {
    setBusy(1);
    return Promise.resolve(enqueue(async () => {
      try {
        if (destroyed) return; // the page went away before this request's turn came
        const { result, error } = await sendInChunks(action, keys, changes);
        if (destroyed) return;
        if (result) {
          result.skipped = [...preSkipped, ...(result.skipped || [])];
          forgetMissing(result);
          let applied;
          try { applied = opts.onApplied && opts.onApplied(result); } catch (err) { console.error('[bulk-actions] onApplied:', err); }
          const ctx = messageContext(changes);
          const done = appliedMessage(result, ctx);
          if (done) showToast(done, 'success');
          const skipped = skippedMessage(result.skipped, ctx);
          if (skipped) showToast(skipped.text, skipped.type);
          try { await applied; } catch (err) { console.error('[bulk-actions] onApplied:', err); }
        }
        if (error) {
          showToast(error.message || (action === 'delete' ? 'Failed to delete tasks' : 'Failed to update tasks'), 'error');
          try { await (opts.onFailed && opts.onFailed(error, { action, task_keys: keys, changes })); } catch (err) { console.error('[bulk-actions] onFailed:', err); }
        }
      } finally {
        setBusy(-1);
      }
    }));
  }

  // At most BULK_MAX_TASKS keys per request. A failed chunk stops the rest: `result` holds
  // what the earlier chunks applied (null when the first one failed), `error` the failure.
  async function sendInChunks(action, keys, changes) {
    let merged = null;
    for (let i = 0; i < keys.length; i += BULK_MAX_TASKS) {
      const chunk = keys.slice(i, i + BULK_MAX_TASKS);
      try {
        const res = action === 'delete'
          ? await api.post(`/projects/${projectId}/tasks/bulk-delete`, { task_keys: chunk })
          : await api.patch(`/projects/${projectId}/tasks/bulk`, { task_keys: chunk, changes });
        merged = mergeBulkResults(merged, res);
      } catch (error) {
        return { result: merged && { action, task_keys: keys, changes, ...merged }, error };
      }
    }
    return { result: merged && { action, task_keys: keys, changes, ...merged }, error: null };
  }

  // Keys that are gone for good — deleted (with their subtrees) or unknown to the server —
  // leave the selection before the host repaints.
  function forgetMissing(result) {
    const gone = [
      ...(result.deleted || []),
      ...(result.descendants_deleted || []),
      ...(result.skipped || []).filter(s => s.reason === 'not_found').map(s => s.task_key),
    ];
    if (gone.length) selection.deleteAll(gone);
  }

  function setBusy(delta) {
    busy = Math.max(0, busy + delta);
    paintBusy();
  }

  // While a request is out, the selects and Delete are disabled (Clear stays live) and a thin
  // progress line runs along the toolbar's bottom edge.
  function paintBusy() {
    if (!el) return;
    const on = busy > 0;
    el.classList.toggle('is-busy', on);
    el.setAttribute('aria-busy', String(on));
    el.querySelectorAll('select, [data-bulk-action="delete"]').forEach((control) => { control.disabled = on; });
  }

  // Re-reads the host's data into a shown toolbar — the sprint numbers in use change as tasks
  // move — without rebuilding it, so a focused field keeps its focus and text.
  function refresh() {
    if (destroyed) return;
    if (!el) { sync(); return; }
    const sprintSelect = el.querySelector('select[data-bulk="priority"]');
    if (sprintSelect) sprintSelect.innerHTML = sprintOptions();
    paintStatusOptions();
    if (assigneeCombo) assigneeCombo.refresh();
    sync();
  }

  function paintStatusOptions() {
    const select = el && el.querySelector('select[data-bulk="status"]');
    if (select) select.innerHTML = statusSelectOptions(statuses);
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    unsubscribe();
    unmount();
    if (modalToken) closeModal(modalToken); // only while this toolbar's confirm is still open
    modalToken = null;
    host.classList.remove('bulk-toolbar-host');
  }

  host.classList.add('bulk-toolbar-host');
  loadStatuses(projectId).then((list) => {
    statuses = list || [];
    paintStatusOptions();
  });
  const unsubscribe = selection.subscribe(sync);
  sync();

  return { refresh, destroy, get element() { return el; } };
}
